import { useState, useCallback, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { inputVariants, type InputProps } from "./input";
import { formatCurrency } from "../../lib/format";

export interface CurrencyInputProps extends Omit<
  InputProps,
  "value" | "onChange" | "type"
> {
  value: number;
  onChange: (value: number) => void;
}

/**
 * Converte string de entrada para centavos
 * Remove tudo que não é número e divide por 100
 */
function parseInputToCents(input: string): number {
  const digits = input.replace(/\D/g, "");
  if (!digits) return 0;
  return parseInt(digits, 10) / 100;
}

export function CurrencyInput({
  className,
  size,
  disabled,
  error,
  value,
  onChange,
  ...props
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState(() => formatCurrency(value));

  // Sincroniza displayValue quando o value externo muda
  useEffect(() => {
    setDisplayValue(formatCurrency(value));
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const numericValue = parseInputToCents(input);
      setDisplayValue(formatCurrency(numericValue));
      onChange(numericValue);
    },
    [onChange],
  );

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    // Seleciona todo o texto ao focar
    e.target.select();
  }, []);

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        R$
      </span>
      <input
        data-slot="currency-input"
        data-disabled={disabled ? "" : undefined}
        data-error={error ? "" : undefined}
        className={twMerge(inputVariants({ size }), "pl-9", className)}
        disabled={disabled}
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        {...props}
      />
    </div>
  );
}
